import { createSlice } from "@reduxjs/toolkit";

// ----------------------------------------------------------------------
// Estado de red del navegador + latencia REAL medida en el interceptor
// de axios. Se alimenta de:
//   - window.navigator.onLine (eventos online / offline)
//   - navigator.connection (Network Information API — Chrome/Edge/Opera)
//   - interceptor de axios en src/utils/axios.js que empuja cada latencia
//     al slice vía `addLatencySample`.
// ----------------------------------------------------------------------

/** Tamaño de la ventana deslizante de latencias (calls API recientes). */
const LATENCY_WINDOW = 20;
/**
 * Ventana deslizante SOLO con muestras de heartbeat (ping al backend cada
 * 60s). Separada de `latencySamples` para que el histórico persistido en
 * `ConnectivitySample` mida la red PURA: sin contaminar con POSTs grandes
 * del TPV (catálogos, pedidos pesados) que distorsionan p95/max sin que
 * la red en sí esté mal.
 *
 * El TopBar y los KPIs en vivo siguen usando `latencySamples` — eso refleja
 * el dolor real del usuario en ese momento; el chart histórico refleja la
 * salud de la conexión en sí.
 */
const HEARTBEAT_WINDOW = 30;

const initialState = {
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  // Ventana deslizante con los ms de las últimas N llamadas al API.
  latencySamples: [],
  // Ventana deslizante con los ms de los últimos N heartbeats.
  heartbeatSamples: [],
  avgLatencyMs: null,
  lastSampleAt: null,

  // Network Information API (puede ser undefined en iOS Safari).
  effectiveType: null, // 'slow-2g' | '2g' | '3g' | '4g'
  downlinkMbps: null,  // ancho de banda estimado
  rttMs: null,         // round-trip time estimado por el navegador

  // Contador de fallos consecutivos (axios error) — un pico sugiere que
  // estamos perdiendo conectividad aunque navigator.onLine siga en true.
  consecutiveFailures: 0,
};

const slice = createSlice({
  name: "networkStatus",
  initialState,
  reducers: {
    setOnline(state, action) {
      state.isOnline = action.payload;
      if (action.payload) {
        // Al recuperar la red, limpiamos el contador de fallos; la siguiente
        // sample válida lo mantendrá en 0.
        state.consecutiveFailures = 0;
      }
    },
    setConnectionInfo(state, action) {
      const { effectiveType, downlink, rtt } = action.payload || {};
      state.effectiveType = effectiveType ?? state.effectiveType;
      state.downlinkMbps = downlink ?? state.downlinkMbps;
      state.rttMs = rtt ?? state.rttMs;
    },
    // Fase 3 · Lote de muestras (el cliente HTTP agrupa y despacha como
    // mucho una vez cada 2 s).
    addLatencySamples(state, action) {
      const list = Array.isArray(action.payload) ? action.payload : [];
      let added = 0;
      for (const raw of list) {
        const ms = Number(raw);
        if (!Number.isFinite(ms) || ms < 0) continue;
        state.latencySamples.push(ms);
        added++;
      }
      if (added === 0) return;
      while (state.latencySamples.length > LATENCY_WINDOW) state.latencySamples.shift();
      state.avgLatencyMs =
        state.latencySamples.reduce((a, b) => a + b, 0) / state.latencySamples.length;
      state.lastSampleAt = new Date().toISOString();
      state.consecutiveFailures = 0;
    },
    addLatencySample(state, action) {
      const ms = Number(action.payload);
      if (!Number.isFinite(ms) || ms < 0) return;
      state.latencySamples.push(ms);
      if (state.latencySamples.length > LATENCY_WINDOW) {
        state.latencySamples.shift();
      }
      state.avgLatencyMs =
        state.latencySamples.reduce((a, b) => a + b, 0) / state.latencySamples.length;
      state.lastSampleAt = new Date().toISOString();
      state.consecutiveFailures = 0;
    },
    // Ping aislado al backend → mide red pura. Va a un buffer separado
    // que solo el reporter de calidad consume para persistir. NO reseteamos
    // `consecutiveFailures` aquí porque el ping convive con tráfico real
    // y queremos que los failures de tráfico real (cuenta el interceptor
    // de axios) tampoco se anulen por un heartbeat OK.
    addHeartbeatSample(state, action) {
      const ms = Number(action.payload);
      if (!Number.isFinite(ms) || ms < 0) return;
      state.heartbeatSamples.push(ms);
      if (state.heartbeatSamples.length > HEARTBEAT_WINDOW) {
        state.heartbeatSamples.shift();
      }
    },
    // Llamada por el flush del reporter tras enviar un batch exitoso al
    // backend. Sin esto, los samples se contarían N veces en el bucket
    // horario (el backend hace merge sumando sampleCount).
    clearHeartbeatSamples(state) {
      state.heartbeatSamples = [];
    },
    addFailureSample(state) {
      state.consecutiveFailures += 1;
    },
  },
});

export default slice.reducer;
export const {
  setOnline,
  setConnectionInfo,
  addLatencySample,
  addLatencySamples,
  addHeartbeatSample,
  clearHeartbeatSamples,
  addFailureSample,
} = slice.actions;
